import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  useLazyGetPdfJobLogsQuery,
  type PdfJobLogEvent,
} from "features/pdfTools/pdfTools";

const POLL_MS = 3000;
const MAX_LINES = 4000;

const ERROR_RX = /(?:\b|^)(ERROR|CRITICAL|Traceback|Exception)\b/i;

function formatTs(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString();
}

function LogLine({ event }: { event: PdfJobLogEvent }) {
  const text = event.message ?? "";
  const isError = ERROR_RX.test(text);
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        py: 0.15,
        px: 0.5,
        borderRadius: 0.5,
        "&:hover": { bgcolor: "#f5f5f5" },
        fontFamily: "monospace",
        fontSize: "0.78rem",
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: isError ? "#d32f2f" : "#1a1a1a",
      }}
    >
      {event.timestamp ? (
        <Box
          component="span"
          sx={{ flexShrink: 0, color: "#5f6b7a", fontSize: "0.74rem" }}
        >
          {formatTs(event.timestamp)}
        </Box>
      ) : null}
      <Box component="span" sx={{ flex: 1 }}>
        {text}
      </Box>
    </Box>
  );
}

interface JobLogsPanelProps {
  jobId: string;
  /** Keep polling while the job is running. */
  live: boolean;
}

export default function JobLogsPanel({ jobId, live }: JobLogsPanelProps) {
  const [fetchLogs] = useLazyGetPdfJobLogsQuery();
  const [events, setEvents] = useState<PdfJobLogEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const nextTokenRef = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    setEvents([]);
    setLoaded(false);
    nextTokenRef.current = null;

    const tick = async () => {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const res = await fetchLogs({
          jobId,
          next_token: nextTokenRef.current,
        }).unwrap();
        if (stopped) return;
        setLoaded(true);
        if (res.source === "file") {
          // The mirrored log file is re-read whole each time.
          setEvents(res.events.slice(-MAX_LINES));
          nextTokenRef.current = null;
        } else if (res.events.length > 0 || nextTokenRef.current === null) {
          nextTokenRef.current = res.next_token;
          if (res.events.length > 0) {
            setEvents((prev) => [...prev, ...res.events].slice(-MAX_LINES));
          }
        } else {
          nextTokenRef.current = res.next_token;
        }
      } catch {
        if (!stopped) setLoaded(true);
      } finally {
        inFlight = false;
      }
    };

    void tick();
    if (!live) return () => undefined;
    const interval = setInterval(() => void tick(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [jobId, live, fetchLogs]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const nearBottom =
      box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (nearBottom) box.scrollTop = box.scrollHeight;
  }, [events]);

  return (
    <Box
      ref={boxRef}
      sx={{
        fontFamily: "monospace",
        fontSize: "0.78rem",
        m: 0,
        p: 1.5,
        bgcolor: "#ffffff",
        color: "#1a1a1a",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "auto",
        height: { xs: 320, sm: 400, md: 440 },
      }}
    >
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          {loaded
            ? live
              ? "Waiting for log lines…"
              : "No logs are available for this job."
            : "Loading logs…"}
        </Typography>
      ) : (
        events.map((event, idx) => <LogLine key={idx} event={event} />)
      )}
    </Box>
  );
}
